import
{
    BarChart3,
    Calendar,
    CalendarDays,
    Clock,
    Database,
    History,
    Loader2,
    SkipBack,
    SkipForward,
    Sparkles,
    Target,
    TrendingUp,
    Zap,
} from 'lucide-react'
import React, { startTransition, useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useShallow } from 'zustand/react/shallow'
import { AnalysisMode, IndexSymbol, TimeGap, SUPPORTED_SYMBOLS } from '../../config/constants'
import { useMarketStore } from '../../services/store'
import { useActiveHistory } from '../../hooks/useDataBus'
import { useSettingsStore } from '../../stores/settingsStore'
import { AiInsightsModal } from '../ai/AiInsightsModal'
import { BaseTimePicker } from '../ui/BaseTimePicker'
import { MaterialSelect } from '../ui/MaterialSelect'
import { LiveTickerDisplay } from './LiveTickerDisplay'
import { TimeRangeSlicer } from '../analysis/TimeRangeSlicer'
import { HistoricalDatePicker } from '../ui/HistoricalDatePicker'
import { toIST } from '../../lib/data-utils'

const EMPTY_ARRAY: string[] = []

const MIN_TIME = 9 * 60 + 15
const MAX_TIME = 15 * 60 + 30

const timeToMinutes = ( timeStr: string ): number =>
{
    if ( !timeStr || !timeStr.includes( ':' ) ) return MIN_TIME
    const parts = timeStr.split( ':' )
    const h = Number( parts[ 0 ] || 0 )
    const m = Number( parts[ 1 ] || 0 )
    return h * 60 + m
}

const minutesToTime = ( minutes: number ): string =>
{
    const h = Math.floor( minutes / 60 )
    const m = Math.floor( minutes % 60 )
    return `${h.toString().padStart( 2, '0' )}:${m.toString().padStart( 2, '0' )}`
}

const gapToMinutes = ( gap: TimeGap ): number =>
{
    switch ( gap )
    {
        case TimeGap.M1:
            return 1
        case TimeGap.M5:
            return 5
        case TimeGap.M10:
            return 10
        case TimeGap.M15:
            return 15
        case TimeGap.M30:
            return 30
        case TimeGap.H1:
            return 60
        case TimeGap.H2:
            return 120
        case TimeGap.H4:
            return 240
        default:
            return 5
    }
}

const normalizeDateStr = (d: string | null) => {
    if (!d) return ''
    let str = d
    if (str.includes('T')) str = str.split('T')[0] || str
    if (str.includes(' ')) str = str.split(' ')[0] || str
    // If YYYY-MM-DD, convert to DD-MM-YYYY
    const parts = str.split('-')
    if (parts.length === 3 && parts[0] && parts[0].length === 4) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`
    }
    return str
}

export const HeaderControls: React.FC = () =>
{
    const settings = useSettingsStore(
        useShallow( ( state ) => ( {
            symbol: state.symbol,
            expiry: state.expiry,
            range: state.range,
            timeGap: state.timeGap,
            simulationTime: state.simulationTime,
            baseTime: state.baseTime,
            mode: state.mode,
            isLive: state.isLive,
            historicalDate: state.historicalDate,
            datasetDate: state.datasetDate,
            inspectionSource: state.inspectionSource,
            setSymbol: state.setSymbol,
            setExpiry: state.setExpiry,
            setRange: state.setRange,
            setTimeGap: state.setTimeGap,
            setSimulationTime: state.setSimulationTime,
            setBaseTime: state.setBaseTime,
            setMode: state.setMode,
            setIsLive: state.setIsLive,
            setHistoricalDate: state.setHistoricalDate,
        } ) ),
    )

    // From MarketStore/Service
    const expiryList = useMarketStore( ( state ) => state.expiryMap[ settings.symbol ] || EMPTY_ARRAY )
    const isExpiryLoading = expiryList.length === 0

    const [ isAiOpen, setIsAiOpen ] = useState( false )

    const [ localRange, setLocalRange ] = useState<number | string>( settings.range )

    useEffect( () =>
    {
        setLocalRange( settings.range )
    }, [ settings.range ] )

    const historyData = useActiveHistory(settings.symbol);

    const availableTimes = useMemo( () =>
    {
        if ( !historyData || historyData.length === 0 ) return []

        return historyData
            .map( ( row: any ) =>
            {
                const timeVal = row.timestampIST || row.timestamp || row.time
                if ( !timeVal ) return null

                // Expects epoch or "YYYY-MM-DDTHH:mm:ss"
                const istStr = toIST( timeVal )
                const timePart = istStr
                    .split( /T| / )[ 1 ]
                    ?.replace( /[+Z].*$/, '' )
                    .substring( 0, 5 )
                return timePart || null
            } )
            .filter( Boolean ) as string[]
    }, [ historyData, settings.symbol, settings.historicalDate ] )

    // Safety for baseTime rendering
    const baseTimeParts = ( settings.baseTime || '09:16' ).split( ':' ).map( Number )
    const baseH = baseTimeParts[ 0 ]
    const baseM = baseTimeParts[ 1 ]
    const isBaseTimeValid = baseH !== undefined && baseM !== undefined

    // Removed local handleTimeJump for old slicer

    const handleRangeChange = ( e: React.ChangeEvent<HTMLInputElement> ) =>
    {
        setLocalRange( e.target.value )
    }

    const handleRangeCommit = () =>
    {
        const val = parseInt( localRange.toString(), 10 )
        if ( !Number.isNaN( val ) )
        {
            const clamped = Math.min( Math.max( val, 1 ), 100 )
            settings.setRange( clamped )
            setLocalRange( clamped )
        } else
        {
            setLocalRange( settings.range )
        }
    }

    // Removed localSlider code

    const expiryOptions = useMemo( () =>
    {
        return expiryList.map( ( date ) => ( {
            value: date,
            label: settings.inspectionSource === 'HistoricalSnapshot' ? `${date} (Hist)` : date
        } ) )
    }, [ expiryList, settings.inspectionSource ] )

    // Auto-select first expiry if current selection is invalid for the list
    useEffect( () =>
    {
        if ( expiryList.length > 0 )
        {
            // If current expiry is empty OR not in the new list, select the first one.
            if ( !settings.expiry || !expiryList.includes( settings.expiry ) )
            {
                const firstExpiry = expiryList[ 0 ]
                if ( firstExpiry )
                {
                    settings.setExpiry( firstExpiry )
                }
            }
        }
    }, [ expiryList, settings.expiry, settings.setExpiry ] )

    return (
        <div className="flex flex-col w-full transition-all duration-300 relative z-20 pointer-events-none mt-1 mb-2">
            <div className="w-full flex justify-center overflow-visible px-2 sm:px-4 py-1">
                
                {/* 🌟 FLOATING ALIGNED PILLS (NO BACKGROUND) 🌟 */}
                <motion.div 
                    initial={{ y: -20, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    className="pointer-events-auto flex flex-row items-center w-full justify-center gap-1.5 lg:gap-3 flex-wrap md:flex-nowrap"
                >
                    
                    {/* SECTION 1: Symbol */}
                    <MaterialSelect
                        value={ settings.symbol }
                        onChange={ ( val ) => setTimeout( () => settings.setSymbol( val as any ), 0 ) }
                        options={ SUPPORTED_SYMBOLS.map( ( s ) => ( { value: s, label: s } ) ) }
                        icon={<TrendingUp className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-slate-500 shrink-0" />}
                        className="flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full transition-colors w-auto min-w-[80px] lg:min-w-[100px] text-[12px] lg:text-[14px] font-bold premium-cell-mono text-slate-800 dark:text-slate-100 outline-none cursor-pointer border border-slate-200/80 dark:border-slate-700 shadow-sm shrink-0"
                        variant="custom"
                    />
                    
                    {/* SECTION 2: Expiry */}
                    <MaterialSelect
                        value={ settings.expiry || '' }
                        onChange={ ( val ) => setTimeout( () => settings.setExpiry( val ), 0 ) }
                        options={ expiryOptions }
                        icon={isExpiryLoading ? <Loader2 className="w-3.5 lg:w-4 h-3.5 lg:h-4 animate-spin text-slate-500 shrink-0" /> : <Calendar className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-slate-500 shrink-0" />}
                        className="flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full transition-colors w-auto min-w-[100px] lg:min-w-[120px] text-[12px] lg:text-[14px] font-bold premium-cell-mono text-slate-800 dark:text-slate-100 outline-none cursor-pointer border border-slate-200/80 dark:border-slate-700 shadow-sm shrink-0"
                        disabled={ isExpiryLoading }
                        variant="custom"
                    />

                    {/* SECTION 3: ATM */}
                    <div className="hidden md:flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full transition-colors border border-slate-200/80 dark:border-slate-700 shadow-sm group w-auto shrink-0">
                        <Target className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-slate-400 shrink-0 group-hover:text-slate-600 transition-colors" />
                        <span className="text-[11px] lg:text-[12px] font-bold text-slate-500">ATM±</span>
                        <input
                            type="number"
                            min={ 1 }
                            max={ 100 }
                            value={ localRange }
                            onChange={ handleRangeChange }
                            onBlur={ handleRangeCommit }
                            onKeyDown={ ( e ) => e.key === 'Enter' && handleRangeCommit() }
                            className="bg-transparent text-slate-800 dark:text-slate-100 text-[13px] lg:text-[15px] font-bold w-[28px] lg:w-[36px] outline-none text-center premium-cell-mono p-0 focus:text-emerald-600"
                        />
                    </div>

                    {/* SECTION 4: Time */}
                    <div className="hidden md:flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-full transition-colors border border-slate-200/80 dark:border-slate-700 shadow-sm cursor-pointer group w-auto shrink-0">
                        <Clock className="w-3.5 lg:w-4 h-3.5 lg:h-4 text-slate-400 shrink-0 group-hover:text-slate-600 transition-colors" />
                        <BaseTimePicker
                            value={ settings.baseTime || '09:20' }
                            onChange={ ( val ) => settings.setBaseTime( val ) }
                        />
                    </div>
                        
                    {/* SECTION 5: Mode */}
                    <div className="flex items-center bg-white dark:bg-slate-800 rounded-full p-0.5 lg:p-1 border border-slate-200/80 dark:border-slate-700 shadow-sm shrink-0">
                        <button
                            onClick={ () => setTimeout( () => settings.setMode( AnalysisMode.OI ), 0 ) }
                            className={ `px-2.5 lg:px-3.5 py-1 rounded-full text-[11px] lg:text-[12px] font-bold transition-all duration-300 ${settings.mode === AnalysisMode.OI ? 'bg-slate-100 dark:bg-slate-700 text-emerald-700 dark:text-emerald-400 shadow-inner' : 'text-slate-500 hover:text-slate-700'} ` }
                        >
                            OI
                        </button>
                        <button
                            onClick={ () => setTimeout( () => settings.setMode( AnalysisMode.VOLUME ), 0 ) }
                            className={ `px-2.5 lg:px-3.5 py-1 rounded-full text-[11px] lg:text-[12px] font-bold transition-all duration-300 ${settings.mode === AnalysisMode.VOLUME ? 'bg-slate-100 dark:bg-slate-700 text-emerald-700 dark:text-emerald-400 shadow-inner' : 'text-slate-500 hover:text-slate-700'} ` }
                        >
                            VOL
                        </button>
                    </div>

                    {/* SECTION 6: Source */}
                    <div className="relative flex items-center bg-white dark:bg-slate-800 rounded-full p-0.5 lg:p-1 border border-slate-200/80 dark:border-slate-700 shadow-sm shrink-0">
                        {[{id: 'live', label: 'Live', icon: Zap}, {id: 'hist', label: 'Hist', icon: History}].map((opt) => {
                            const isSelected = (opt.id === 'live' && settings.isLive) || (opt.id === 'hist' && !settings.isLive)
                            return (
                                <button
                                    key={opt.id}
                                    onClick={() => setTimeout(() => settings.setIsLive(opt.id === 'live'), 0)}
                                    className={`relative z-10 px-2.5 lg:px-3.5 py-1 rounded-full text-[11px] lg:text-[12px] font-bold flex items-center gap-1 lg:gap-1.5 transition-all duration-300 ${isSelected ? (opt.id === 'live' ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400') : 'text-slate-500 hover:text-slate-700'}`}
                                >
                                    {isSelected && (
                                        <motion.div
                                            layoutId="source-toggle-floating"
                                            className="absolute inset-0 rounded-full bg-slate-100 dark:bg-slate-700 shadow-inner z-[-1]"
                                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                        />
                                    )}
                                    <opt.icon className={`w-3 lg:w-3.5 h-3 lg:h-3.5 ${isSelected && opt.id === 'live' ? 'text-emerald-600 dark:text-emerald-400' : ''}`} /> {opt.label}
                                </button>
                            )
                        })}
                    </div>

                    {/* SECTION 7: Status/Ticker */}
                    { !settings.isLive ? (
                        <>
                            {(() => {
                                const isDateMismatch = settings.datasetDate && normalizeDateStr(settings.datasetDate) !== normalizeDateStr(settings.historicalDate);
                                return (
                                    <>
                                        <div className={`flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 rounded-full border shadow-sm transition-colors hover:opacity-90 group w-auto shrink-0 ${isDateMismatch ? 'bg-red-100 dark:bg-red-900/40 border-red-500/80 dark:border-red-500/80' : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200/60 dark:border-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/50'}`}>
                                            <CalendarDays className={`w-3.5 lg:w-4 h-3.5 lg:h-4 shrink-0 ${isDateMismatch ? 'text-red-600 dark:text-red-400 font-bold animate-pulse' : 'text-amber-600 dark:text-amber-500'}`} />
                                            <HistoricalDatePicker
                                                symbol={ settings.symbol }
                                                currentDateStr={ settings.historicalDate }
                                                onChange={ ( dateStr ) => setTimeout(() => settings.setHistoricalDate( dateStr ), 0) }
                                                className={`flex items-center outline-none bg-transparent border-none p-0 ${isDateMismatch ? 'text-red-700 dark:text-red-300 font-bold' : ''}`}
                                                hideIcon={true}
                                            />
                                        </div>
                                        
                                        {/* SECTION 8: Dataset Date (Parquet Source) */}
                                        {settings.datasetDate && (
                                            <div className={`flex items-center gap-1 lg:gap-1.5 px-2.5 lg:px-3.5 py-1 lg:py-1.5 rounded-full border shadow-sm transition-colors group w-auto shrink-0 relative cursor-help ${isDateMismatch ? 'bg-red-100 dark:bg-red-900/40 border-red-500/80 dark:border-red-500/80' : 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-200/60 dark:border-indigo-800'}`}>
                                                <Database className={`w-3.5 lg:w-4 h-3.5 lg:h-4 shrink-0 ${isDateMismatch ? 'text-red-600 dark:text-red-400 font-bold animate-pulse' : 'text-indigo-500'}`} />
                                                <span className={`text-[11px] lg:text-[12px] font-bold premium-cell-mono ${isDateMismatch ? 'text-red-700 dark:text-red-300' : 'text-indigo-700 dark:text-indigo-400'}`}>
                                                    {settings.datasetDate}
                                                </span>
                                                <div className={`absolute hidden group-hover:block bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-2 py-1 text-white text-[10px] rounded shadow-lg ${isDateMismatch ? 'bg-red-600 font-bold text-[11px]' : 'bg-slate-800'}`}>
                                                    {isDateMismatch ? '⚠ DATES MISMATCH! Data loaded is for a different day!' : 'Data Extracted From File'}
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )
                            })()}
                        </>
                    ) : (
                        <div className="flex shrink-0">
                            <LiveTickerDisplay />
                        </div>
                    )}

                </motion.div>
            </div>

            <AiInsightsModal isOpen={ isAiOpen } onClose={ () => setIsAiOpen( false ) } response={ null } />

            { !settings.isLive && (
                <div className="w-full relative z-999 bg-surfaceVariant/10 dark:bg-slate-900/30 border-b border-gray-100/50 dark:border-slate-800/50 pointer-events-auto">
                    <div className="max-w-screen-2xl mx-auto px-4 py-2 flex flex-wrap items-center justify-center gap-4 md:gap-8">
                        <div className="flex-1 min-w-[300px] max-w-3xl xl:max-w-none">
                            <TimeRangeSlicer
                                minMinutes={ MIN_TIME }
                                maxMinutes={ MAX_TIME }
                                viewMode="snapshot"
                                onViewModeChange={ () => { } }
                                onChange={ ( _start, end ) => {
                                    React.startTransition(() => {
                                        settings.setSimulationTime( end )
                                    })
                                } }
                                availableTimes={ availableTimes }
                                hideModeToggle={ true }
                                forceFullDay={ true }
                                className="bg-white dark:bg-slate-800 p-2 sm:p-3 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            ) }
        </div>
    )
}
